import classNames from 'classnames';
import { memo } from 'react';

import { FileExtensionPreview } from '@air/component-file-extension-preview';
import { Tooltip } from '@air/primitive-tooltip';
import { type Upload } from '@air/redux-uploader';

import { useUploadingData } from '~/components/Upload/hooks/useUploadingData';
import { UPLOADING_FILE } from '~/constants/testIDs';
import { useHover } from '~/hooks/useHover';

export type UploadingFileGalleryCardProps = {
  upload: Upload;
  width: number;
  getClassName?: (props: { isHovering: boolean }) => string;
};

/**
 * This component is used to display uploading file with upload progress and shimmer animation
 */
export const UploadingFileGalleryCard = memo(({ upload, width, getClassName }: UploadingFileGalleryCardProps) => {
  const {
    apiUploadData: { ext, size },
  } = upload;
  const { stateText } = useUploadingData(upload);
  const { handleHover, handleUnhover, isHovering } = useHover();
  const title = upload.file.name;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: mouse events are for hover state tracking only, not interactive actions
    <div
      data-testid={UPLOADING_FILE}
      data-asset-title={title}
      onMouseEnter={handleHover}
      onMouseMove={handleHover}
      onMouseLeave={handleUnhover}
      className="relative flex h-full flex-col"
    >
      <div
        className={classNames(
          'absolute inset-[-6px] cursor-default rounded-4 border-2 border-transparent',
          isHovering && 'ads:bg-hover-subtle bg-gray-3',
          getClassName?.({ isHovering }),
        )}
      />
      <div className="relative flex grow flex-col rounded-4 border ads:border-default border-pigeon-100 ads:bg-raised bg-white p-3">
        <div className="flex grow">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-4 ads:bg-subtle bg-pigeon-100">
            <FileExtensionPreview ext={ext} color="gray" />
          </div>
        </div>
        <div className="relative">
          {!!size && (
            <Tooltip label={title} side="top" sideOffset={4}>
              <div className="pointer-events-auto truncate text-left font-medium ads:text-primary text-pigeon-700 hover:underline">
                {title}
              </div>
            </Tooltip>
          )}
          <p style={{ width }} className="truncate ads:text-metadata text-12 text-pigeon-400">
            {stateText}
          </p>
        </div>
      </div>
    </div>
  );
});

UploadingFileGalleryCard.displayName = 'UploadingFileGalleryCard';
